import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button, Calendar, Icon, Input } from "@elaniintc/goes-design-system";
import { useState, useEffect, useMemo } from "react";
import {
  type MaskitoDateParams,
  maskitoDateOptionsGenerator,
  maskitoStringifyDate,
  maskitoParseDate,
  maskitoTimeOptionsGenerator,
} from "@maskito/kit";
import { useMaskito } from "@maskito/react";
import dayjs from "dayjs";
import { cn } from "@/utils/theme";
import { localDateToIso } from "@/utils/dates";

const params: MaskitoDateParams = {
  mode: "dd/mm/yyyy",
  separator: "/",
};
const options = maskitoDateOptionsGenerator(params);

function clampDate(d: Date, minDate?: Date, maxDate?: Date) {
  if (minDate && d < minDate) return minDate;
  if (maxDate && d > maxDate) return maxDate;
  return d;
}

function fmt(date?: Date) {
  return date
    ? maskitoStringifyDate(date, { separator: "/", mode: "dd/mm/yyyy" })
    : "";
}

// Función para normalizar el valor de horas
const normalizeHoursValue = (value: string, format24: boolean) => {
  if (!value) return "";

  if (format24) {
    // Si es solo números (ej: "1", "23"), convertir a formato HH:MM (ej: "1:00", "23:00")
    if (/^\d{1,2}$/.test(value)) {
      return `${value}:00`;
    }
    // Si ya está en formato HH:MM, mantenerlo
    return value;
  } else {
    // Para formato 12 horas
    if (/^\d{1,2}$/.test(value)) {
      return `${value}:00 AM`;
    }
    return value;
  }
};

function parseTime(
  timeStr: string,
  format24: boolean,
): { hours: number; minutes: number } {
  if (format24) {
    const [h = "0", m = "0"] = timeStr.split(":");
    return {
      hours: Number.parseInt(h, 10) || 0,
      minutes: Number.parseInt(m, 10) || 0,
    };
  }

  const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i.exec(timeStr);
  if (!match) return { hours: 0, minutes: 0 };

  let h = Number.parseInt(match[1], 10) || 0;
  const m = Number.parseInt(match[2], 10) || 0;
  const period = match[3]?.toUpperCase() || "AM";

  if (period === "PM" && h !== 12) h += 12;
  if (period === "AM" && h === 12) h = 0;

  return { hours: h, minutes: m };
}

function applyTimeToDate(date: Date, timeStr: string, format24: boolean): void {
  const { hours, minutes } = parseTime(timeStr, format24);
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(0);
  date.setMilliseconds(0);
}

function prepareDateToSend(
  date: Date,
  captureCurrentTime: boolean,
  localDateToIsoFn: (d: Date) => string | null | undefined,
): string {
  const dateToSend = new Date(date);
  if (captureCurrentTime) {
    const now = new Date();
    dateToSend.setHours(now.getHours());
    dateToSend.setMinutes(now.getMinutes());
    dateToSend.setSeconds(now.getSeconds());
    dateToSend.setMilliseconds(0);
    return localDateToIsoFn(dateToSend) || "";
  }
  return dateToSend.toISOString();
}

function parseValueToDate(value?: string): Date | null {
  if (!value) return null;
  let parsed = dayjs(value).toDate();
  if (Number.isNaN(parsed.getTime())) {
    parsed = dayjs(value, "DD/MM/YYYY", true).toDate();
  }
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatTimeFromDate(date: Date, format24: boolean): string {
  const hours24 = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  if (format24) {
    return `${String(hours24).padStart(2, "0")}:${minutes}`;
  }
  const hours12 = hours24 % 12 || 12;
  const period = hours24 >= 12 ? "PM" : "AM";
  return `${String(hours12).padStart(2, "0")}:${minutes} ${period}`;
}

interface DateInputProps {
  onChange: (date: string) => void;
  value?: string;
  placeholder?: string;
  group?:
    | "down"
    | "down-left"
    | "down-right"
    | "left"
    | "right"
    | "up"
    | "up-left"
    | "up-right"
    | "single"
    | "middle"
    | "middle-right"
    | "middle-left"
    | "middle-up";
  className?: string;
  wrapperClassName?: string;
  minDate?: Date | string | null;
  maxDate?: Date | string | null;
  includeTime?: boolean;
  format24?: boolean;
  captureCurrentTime?: boolean;
  id?: string;
}

export const DateInput = ({
  onChange: onChangeDate,
  value,
  group,
  placeholder = "Seleccionar fecha",
  className,
  wrapperClassName,
  maxDate: maxDateProp,
  minDate: minDateProp,
  format24 = true,
  includeTime = false,
  captureCurrentTime = false,
  id,
}: DateInputProps) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [dateValue, setDateValue] = useState<string>("");
  const [timeValue, setTimeValue] = useState<string>("");

  // Convert minDate and maxDate to Date objects if they are strings
  const minDate = useMemo(() => {
    if (!minDateProp) return undefined;

    const d =
      typeof minDateProp === "string"
        ? dayjs(minDateProp, "DD/MM/YYYY", true).toDate()
        : new Date(minDateProp);

    if (Number.isNaN(d.getTime())) return undefined;
    d.setHours(0, 0, 0, 0);
    return d;
  }, [minDateProp]);

  const maxDate = useMemo(() => {
    if (!maxDateProp) return undefined;

    const d =
      typeof maxDateProp === "string"
        ? dayjs(maxDateProp, "DD/MM/YYYY", true).toDate()
        : new Date(maxDateProp);

    if (Number.isNaN(d.getTime())) return undefined;
    d.setHours(23, 59, 59, 999);
    return d;
  }, [maxDateProp]);

  const maskedInputRef = useMaskito({ options });

  const timeMaskOptions = maskitoTimeOptionsGenerator(
    format24
      ? {
          mode: "HH:MM",
          timeSegmentMinValues: { hours: 0, minutes: 0 },
          timeSegmentMaxValues: { hours: 23, minutes: 59 },
        }
      : {
          mode: "HH:MM AA",
          timeSegmentMinValues: { hours: 1, minutes: 0 },
          timeSegmentMaxValues: { hours: 12, minutes: 59 },
        },
  );

  const timeMask = useMaskito({ options: timeMaskOptions });

  const updateElement = (_key: "hours", newValue: string) => {
    setTimeValue(newValue);
    if (date) {
      const updatedDate = new Date(date);
      applyTimeToDate(updatedDate, newValue, format24);
      onChangeDate(updatedDate.toISOString());
    }
  };

  useEffect(() => {
    const parsed = parseValueToDate(value);

    if (!parsed) {
      setDate(undefined);
      setDateValue("");
      setTimeValue("");
      return;
    }

    const safe = clampDate(parsed, minDate, maxDate);
    setDate(safe);
    setDateValue(fmt(safe));

    if (includeTime) {
      setTimeValue(formatTimeFromDate(safe, format24));
    }

    if (safe.getTime() !== parsed.getTime()) {
      onChangeDate(safe.toISOString());
    }
  }, [value, includeTime]); // eslint-disable-line react-hooks/exhaustive-deps

  let dateInputGroup = group;
  if (includeTime) {
    dateInputGroup = group === "right" ? "middle-up" : "left";
  }

  let timeInputGroup = group;
  if (includeTime) {
    timeInputGroup = group === "left" ? "middle-up" : "right";
  }

  return (
    <div className={cn("flex w-full", wrapperClassName)}>
      <div className="relative w-full">
        <Input
          id={id}
          group={dateInputGroup}
          ref={maskedInputRef}
          className={cn(
            "w-full border border-neutral bg-neutral-object-accent-silent pr-10",
            className,
          )}
          value={dateValue}
          placeholder={placeholder}
          onBlur={() => {
            const parsed = maskitoParseDate(dateValue, params);

            if (!parsed) {
              if (date) {
                setDateValue(fmt(date));
              } else {
                setDateValue("");
                onChangeDate("");
              }
              return;
            }

            const safe = clampDate(parsed, minDate, maxDate);

            if (includeTime && timeValue) {
              applyTimeToDate(safe, timeValue, format24);
            }

            setDate(safe);
            setDateValue(fmt(safe));
            onChangeDate(prepareDateToSend(safe, captureCurrentTime, localDateToIso));
          }}
          onChange={(e) => {
            const raw = e.target.value;
            const parsed = maskitoParseDate(raw, params);

            if (!parsed && raw.length === 0) {
              setDate(undefined);
              setDateValue("");
              onChangeDate("");
              return;
            }

            if (!parsed) {
              setDateValue(raw);
              return;
            }

            if (includeTime && timeValue) {
              applyTimeToDate(parsed, timeValue, format24);
            }

            // Validate against minDate and maxDate
            if (
              (minDate && parsed < minDate) ||
              (maxDate && parsed > maxDate)
            ) {
              setDateValue(raw);
              return;
            }

            setDate(parsed);
            setDateValue(raw);
            onChangeDate(prepareDateToSend(parsed, captureCurrentTime, localDateToIso));
          }}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Seleccionar fecha"
              className="top-1.5 right-2 absolute"
            >
              <Icon type="far" name="calendar" />
            </Button>
          </PopoverTrigger>

          <PopoverContent
            className="w-full overflow-hidden p-0 bg-neutral-object border-neutral"
            align="end"
            data-testid="date-input-popover"
          >
            <Calendar
              mode="single"
              selected={date}
              captionLayout="dropdown"
              defaultMonth={
                date ? clampDate(date, minDate, maxDate) : new Date()
              }
              disabled={(date) => {
                const normalizedDate = new Date(date);
                normalizedDate.setHours(0, 0, 0, 0);

                if (minDate) {
                  const normalizedMin = new Date(minDate);
                  normalizedMin.setHours(0, 0, 0, 0);
                  if (normalizedDate < normalizedMin) return true;
                }

                if (maxDate) {
                  const normalizedMax = new Date(maxDate);
                  normalizedMax.setHours(0, 0, 0, 0);
                  if (normalizedDate > normalizedMax) return true;
                }

                return false;
              }}
              className="bg-transparent w-full"
              onSelect={(d?: Date) => {
                if (!d) return;
                const safe = clampDate(d, minDate, maxDate);

                if (date && includeTime) {
                  safe.setHours(date.getHours());
                  safe.setMinutes(date.getMinutes());
                  safe.setSeconds(0);
                  safe.setMilliseconds(0);
                }

                setDate(safe);
                setDateValue(fmt(safe));
                onChangeDate(prepareDateToSend(safe, captureCurrentTime, localDateToIso));
                setOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
      {includeTime && (
        <Input
          group={timeInputGroup}
          className={group === "left" || group === "right" ? "w-1/2" : "w-1/3"}
          placeholder="Elige una hora"
          value={timeValue}
          ref={timeMask}
          onChange={(e) => {
            updateElement("hours", e.target.value);
          }}
          onBlur={(e) => {
            let normalizedValue = normalizeHoursValue(e.target.value, format24);

            // Si es formato 12h y el valor no tiene AM/PM, agregar AM automáticamente
            if (
              !format24 &&
              normalizedValue &&
              !/AM|PM/i.test(normalizedValue)
            ) {
              if (/^\d{1,2}:\d{2}$/.test(normalizedValue)) {
                normalizedValue = `${normalizedValue} AM`;
              }
            }

            if (normalizedValue !== e.target.value) {
              updateElement("hours", normalizedValue);
            }
          }}
        />
      )}
    </div>
  );
};
